import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { isString } from 'lodash';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { surfaceApiErrors } from '../../../utils/apiFieldErrors';

const createSalesCreditNote = async ({ payload }) => {
  const profileId = payload?.accounting_supplier_party?.profile_id;
  if (!profileId) {
    throw new Error('Supplier Profile ID is required to create a sales credit note');
  }

  const response = await apiClient.post(`/sales-credit-notes/${profileId}`, payload);
  return response.data;
};

const createPurchaseCreditNote = async ({ payload }) => {
  const profileId = payload?.accounting_customer_party?.profile_id;
  if (!profileId) {
    throw new Error('Customer Profile ID is required to create a purchase credit note');
  }

  const response = await apiClient.post(`/purchase-credit-notes/${profileId}`, payload);
  return response.data;
};

export const useCreateCreditNoteHandler = ({ onSuccess, isPurchase = false }) => {
  const dispatch = useDispatch();
  const { snackbarMessages } = useTranslatedMessages();
  const creditNoteMessages = snackbarMessages.credit_notes ?? {};
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: isPurchase ? createPurchaseCreditNote : createSalesCreditNote,
    onSuccess: (data, variables, context) => {
      dispatch(showSnackbar({ message: creditNoteMessages.credit_note_resubmitted_success, severity: 'success' }));
      queryClient.invalidateQueries({ queryKey: [isPurchase ? 'purchase-credit-notes' : 'sales-credit-notes'] });
      onSuccess && onSuccess(variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        const apiErrors = error.response?.data?.errors;
        if (apiErrors) {
          surfaceApiErrors({
            setFormError: setError,
            dispatch,
            showSnackbarAction: showSnackbar,
            apiErrors,
          });
          return;
        }
        dispatch(
          showSnackbar({
            message: error.response?.data?.message ?? error.message ?? 'An error occurred.',
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            message: isString(error.message) ? error.message : JSON.stringify(error.message),
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = async (payload) => {
    mutation.mutate({ payload });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};
